import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CardContent,
  Card,
  Tab,
  Tabs,
  Button,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import { DataGridComponent } from "../../common/DataGridComponent";
import { useEffect, useState } from "react";
import { ColumnType } from "../../../types/dataGridTypes";
import AddComponentForm from "./AddComponentForm";
import AssignComponentForm from "./AssignComponentForm";
import { useEntity } from "../MetadataPage/useEntity";
import EditFieldDetails from "./EditFieldDetails";
import { useUiConfig } from "./useUiConfig";

const UiConfigPage = () => {
  const componentTypesArray = [
    "List Component",
    "Details Component",
    "Page Layout",
  ];
  const tabs: any = [
    {
      entity_name: "Study",
      path: "/studies",
    },
    {
      entity_name: "Study Details",
      path: "/study/{study-id}",
    },
    {
      entity_name: "Study > Site",
      path: "/study/site",
    },
    {
      entity_name: "Study > Site Details",
      path: "/study/site/{site-id}",
    },
    {
      entity_name: "Study > Vendor",
      path: "/study/vendor",
    },
    {
      entity_name: "Study > Vendor Details",
      path: "/study/vendor/{vendor-id}",
    },
    {
      entity_name: "Study > Central Lab",
      path: "/study/central-lab",
      details_path: "/study/central-lab/{central-lab-id}",
    },
    {
      entity_name: "Study > Central Lab Details",
      path: "/study/central-lab/{central-lab-id}",
    },
    {
      entity_name: "Accession",
      path: "/accessions",
    },
    {
      entity_name: "Accession Details",
      path: "/accessions/{accession-id}",
    },
    {
      entity_name: "Enrollment",
      path: "/enrollment",
    },
    {
      entity_name: "Enrollment Details",
      path: "/enrollment/{enrollment-id}",
    },
    {
      entity_name: "Inventory",
      path: "/inventory",
    },
    {
      entity_name: "Inventory Details",
      path: "/inventory/{inventory-id}",
    },
    {
      entity_name: "Reports",
      path: "/reports",
    },
    {
      entity_name: "Reports Details",
      path: "/reports/{report-id}",
    },
    {
      entity_name: "Sites,Labs & Vendors",
      path: "/sites-lab",
    },
    {
      entity_name: "Sites,Labs & Vendors Details",
      path: "/sites-lab/{id}",
    },
  ];

  const listColumns: ColumnType[] = [
    {
      field: "field",
      headerName: "Field",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "headerName",
      headerName: "Column Name",
      minWidth: 120,
    },
    {
      field: "cmp",
      headerName: "Display Type",
      type: "singleSelect",
      valueOptions: [
        "link",
        "button",
        "chip",
        "icon",
        "actions",
        "dateTime",
        "date",
        "array",
      ],
    },
    {
      field: "type",
      headerName: "Column Type",
      type: "singleSelect",
      valueOptions: [
        "number",
        "singleSelect",
        "multiSelect",
        "boolean",
        "dateTime",
        "date",
      ],
    },
    {
      field: "width",
      headerName: "Width",
    },
    {
      field: "flex",
      headerName: "Flex",
    },
    {
      field: "sortable",
      headerName: "Sortable",
    },
    {
      field: "filterable",
      headerName: "Filterable",
    },
    {
      field: "isCodelistValue",
      headerName: "Codelist Value",
      minWidth: 200,
    },
  ];

  const detailsColumns: ColumnType[] = [
    {
      field: "label",
      headerName: "Field",
      width: 300,
    },
    {
      field: "size",
      headerName: "Size",
      description: "Size 12 takes the entire width of the screen",
      width: 200,
    },
  ];

  const pageLayoutColumns: ColumnType[] = [
    {
      field: "components",
      headerName: "Component(s)",
      width: 200,
    },
    {
      field: "size",
      headerName: "Size",
      description: "Size 12 takes the entire width of the screen",
      width: 200,
    },
  ];
  const [columns, setColumns] = useState(listColumns);
  const [value, setValue] = useState(0);

  const [activeTab, setActiveTab] = useState<any>();
  const [addComponentPopup, setAddComponentPopup] = useState(false);
  const [assignComponentPopup, setAssignComponentPopup] = useState(false);
  const [viewType, setViewType] = useState(componentTypesArray[0]);
  const [entityMetadataList, setEntityMetadataList] = useState([]);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldEditPopup, setFieldEditPopup] = useState(false);
  const [selectedFieldData, setSelectedFieldData] = useState<any>();
  const {
    getAllListComponents,
    getAllListComponentsData,
    getAllDetailsComponents,
    getAllDetailsComponentsData,
    getAllPageLayout,
    getAllPageLayoutData,
  } = useUiConfig();

  const [pageLayoutRows, setPageLayoutRows] = useState([]);

  const {
    refetchGetAllEntities,
    getAllEntitiesData,
    getSelectedEntityDetails,
    selectEntityData,
    selectEntityIsLoading,
  } = useEntity();

  useEffect(() => {
    refetchGetAllEntities();
  }, []);

  useEffect(() => {
    if (getAllEntitiesData && getAllEntitiesData.length > 0) {
      setEntityMetadataList(
        getAllEntitiesData.map((item: any) => item.entity_name)
      );
    }
  }, [getAllEntitiesData]);

  const handleChangeViewType = (e: any) => {
    setViewType(e.target.value);
    if (e.target.value == "List Component") {
      setColumns(listColumns);
      setActiveTab((components.listComponent[0] as any).component_name);
    } else if (e.target.value == "Details Component") {
      setColumns(detailsColumns);
      setActiveTab((components.detailsComponent[0] as any)?.component_name);
    } else if (e.target.value == "Page Layout") {
      setColumns(pageLayoutColumns);
      setActiveTab(tabs[0].entity_name);
    }
    setValue(0);
  };

  const handleChange = (event: any, newValue: number) => {
    setValue(newValue);
    setActiveTab(event.target.id);
  };

  const activeTabDetails = tabs?.find(
    (item: any) => item.entity_name == activeTab
  );

  const [components, setComponents] = useState({
    listComponent: [],
    detailsComponent: [],
  });

  const detailsActiveComponentData: any = components.detailsComponent?.find(
    (item: any) => item.component_name == activeTab
  );

  const cardFields = [
    {
      field: "card_title",
      label: "Card Title",
      value: detailsActiveComponentData?.title,
    },
    {
      field: "card_cation",
      label: "Card Caption",
      value: detailsActiveComponentData?.caption,
    },
    {
      field: "card_chip",
      label: "Card Chip",
      value: detailsActiveComponentData?.chip,
    },
    {
      field: "card_description",
      label: "Card Description",
      value: detailsActiveComponentData?.description,
    },
  ];

  const viewTypeComponent =
    viewType == "List Component"
      ? components.listComponent
      : viewType == "Details Component"
      ? components.detailsComponent
      : tabs;

  useEffect(() => {
    if (viewType == "List Component") {
      const len = components.listComponent.length;
      const temp: any = components.listComponent[len - 1];
      setActiveTab(temp?.component_name || temp?.entity_name);
      setValue(len - 1);
    } else if (viewType == "Details Component") {
      const len = components.detailsComponent.length;
      const temp: any = components.detailsComponent[len - 1];
      setActiveTab(temp?.component_name || temp?.entity_name);
      setValue(len - 1);
    }
  }, [components]);

  useEffect(() => {
    if (activeTab) {
      if (allRows[activeTab] && allRows[activeTab].length > 0) {
        setRows(
          allRows[activeTab].map((item: any, index: any) => {
            return { ...item, id: index };
          })
        );
      }
    }
  }, [activeTab, allRows]);

  const [selectedComponentName, setSelectedComponentName] = useState("");

  const getMetadataFields = (data: any) => {
    getSelectedEntityDetails({ table_name: data.selected_entity });
    setSelectedComponentName(data.component_name);
  };

  useEffect(() => {
    if (selectEntityData && selectEntityData?.fields) {
      const entityRows = selectEntityData?.fields.map((item: any) => {
        return { id: item.field_name, column_name: item.record_id_display };
      });
      const temp: any = {};
      temp[selectedComponentName] = entityRows;
      setAllRows({ ...allRows, ...temp });
    }
  }, [selectEntityData]);

  useEffect(() => {
    if (selectEntityIsLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [selectEntityIsLoading]);

  const actions = {
    field: (data: any) => {
      setFieldEditPopup(true);
      setSelectedFieldData(data);
    },
  };

  const handleSaveField = (data: any) => {
    const temp: any = {};
    temp[activeTab] = [
      ...allRows[activeTab].map((item: any) => {
        if (item.id != data.id) {
          return item;
        } else {
          return { ...item, ...data };
        }
      }),
    ];
    const updatedAllRows = { ...allRows, ...temp };
    setAllRows(updatedAllRows);
    setFieldEditPopup(false);
  };

  useEffect(() => {
    if (viewType == "List Component") {
      getAllListComponents({});
    }
    if (viewType == "Details Component") {
      getAllDetailsComponents({});
    }
    if (viewType == "Page Layout") {
      getAllPageLayout({});
    }
  }, [viewType]);

  useEffect(() => {
    if (getAllListComponentsData && getAllListComponentsData.length > 0) {
      setComponents({
        ...components,
        listComponent: [
          ...components.listComponent,
          ...getAllListComponentsData,
        ] as any,
      });

      const temp: any = {};
      getAllListComponentsData?.forEach(
        (item: any) => (temp[item.component_name] = item.fields)
      );
      setAllRows({
        ...allRows,
        ...temp,
      });
    }
  }, [getAllListComponentsData]);

  useEffect(() => {
    if (getAllDetailsComponentsData && getAllDetailsComponentsData.length > 0) {
      setComponents({
        ...components,
        detailsComponent: [
          ...components.detailsComponent,
          ...getAllDetailsComponentsData,
        ] as any,
      });

      const temp: any = {};
      getAllDetailsComponentsData?.forEach(
        (item: any) => (temp[item.component_name] = item.content)
      );
      setAllRows({
        ...allRows,
        ...temp,
      });
    }
  }, [getAllDetailsComponentsData]);

  useEffect(() => {
    if (
      viewType == "Page Layout" &&
      getAllPageLayoutData &&
      getAllPageLayoutData.length > 0
    ) {
      if (getAllPageLayoutData.find((item: any) => item?.page == activeTab)) {
        setPageLayoutRows(
          getAllPageLayoutData
            .find((item: any) => item?.page == activeTab)
            .content?.map((item: any, index: any) => {
              return { ...item, id: index };
            })
        );
      } else {
        setPageLayoutRows([]);
      }
    }
  }, [getAllPageLayoutData, activeTab]);

  return (
    <>
      <Card>
        <CardContent sx={{ pb: "16px !important" }}>
          <Grid container spacing={2} mb={-1} alignItems="center">
            <Grid item md={6}>
              <FormControl>
                <FormLabel>Select Category</FormLabel>
                <RadioGroup
                  row
                  onChange={handleChangeViewType}
                  value={viewType}
                >
                  {componentTypesArray?.map((option: any) => (
                    <FormControlLabel
                      key={option}
                      control={<Radio />}
                      value={option}
                      label={option}
                      id={option}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs>
              {viewType == "Page Layout" && (
                <>
                  <FormLabel>Path</FormLabel>
                  <Typography variant="body1" gutterBottom className="wordWrap">
                    {activeTabDetails?.path}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid item>
              {viewType != "Page Layout" && (
                <Button
                  variant="contained"
                  onClick={() => setAddComponentPopup(true)}
                >
                  Add Component
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {viewType == "Page Layout" && (
        <Box className="dFlex justifyContentEnd" sx={{ position: "relative" }}>
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              zIndex: 100,
              top: "32px",
              right: 0,
              minWidth: "max-content",
            }}
            onClick={() => setAssignComponentPopup(true)}
          >
            Assign Component
          </Button>
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "background.paper",
          display: "flex",
          mt: 3,
          maxHeight: "calc(100vh - 340px)",
        }}
      >
        {viewTypeComponent.length > 0 && (
          <>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={value}
              onChange={handleChange}
              aria-label="KIIT UI Config"
              sx={{
                borderRight: 1,
                borderColor: "divider",
                width: "220px",
                maxWidth: "220px",
                flex: 1,
                "& .MuiTab-root": {
                  '&[aria-selected="true"]': {
                    background: "rgba(36, 63, 129, 0.07)",
                  },
                },
              }}
            >
              {viewTypeComponent?.map((item: any, index: number) => (
                <Tab
                  label={item.component_name || item.entity_name}
                  key={index}
                  id={item.component_name || item.entity_name}
                  sx={{
                    alignItems: "flex-start",
                    textAlign: "left",
                    wordBreak: "break-word",
                  }}
                />
              ))}
            </Tabs>

            <div
              role="tabpanel"
              style={{
                flex: 1,
                maxWidth: "calc(100% - 220px)",
              }}
            >
              <Box
                sx={{
                  ml: 2,
                  maxWidth: "100%",
                }}
              >
                {viewType == "Details Component" && (
                  <Grid container spacing={3}>
                    {cardFields.map((item, index) => (
                      <Grid item md={6} key={index}>
                        <TextField
                          key={item.field}
                          label={item.label}
                          variant="standard"
                          // disabled
                          fullWidth
                          defaultValue={item.value || ""}
                          value={item.value || ""}
                          InputLabelProps={{ shrink: item.value }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
                {activeTab && viewType && (
                  <Box
                    sx={{
                      width: "100%",
                      flex: 1,
                      // maxHeight: "calc(100vh - 200px)",
                    }}
                  >
                    <DataGridComponent
                      checkboxSelection={viewType != "Page Layout"}
                      hasDefinedColumns={false}
                      columns={columns as any}
                      rows={viewType == "Page Layout" ? pageLayoutRows : rows}
                      autoHeight
                      columnVisibility={{
                        column_type: false,
                        codelist_associated: false,
                      }}
                      loading={isLoading}
                      hideFooter
                      hideFooterPagination
                      hideFooterRowCount
                      sx={{
                        "& .MuiDataGrid-toolbarContainer, & .MuiTablePagination-root":
                          {
                            display: "none",
                          },
                      }}
                      actions={actions}
                    />
                  </Box>
                )}
              </Box>
            </div>
          </>
        )}
      </Box>
      {addComponentPopup && (
        <AddComponentForm
          addComponentPopup={addComponentPopup}
          setAddComponentPopup={setAddComponentPopup}
          setComponents={setComponents}
          components={components}
          viewType={viewType}
          entityMetadataList={entityMetadataList}
          setAllRows={setAllRows}
          allRows={allRows}
          getMetadataFields={getMetadataFields}
        />
      )}
      {assignComponentPopup && (
        <AssignComponentForm
          assignComponentPopup={assignComponentPopup}
          setAssignComponentPopup={setAssignComponentPopup}
          components={components}
          setRows={setRows}
          rows={rows}
        />
      )}
      {fieldEditPopup && (
        <EditFieldDetails
          fieldEditPopup={fieldEditPopup}
          setFieldEditPopup={setFieldEditPopup}
          selectedFieldData={selectedFieldData}
          handleSaveField={handleSaveField}
        />
      )}
    </>
  );
};

export default UiConfigPage;
